import { useContext, useEffect } from 'react';

import { ClientNames, ProviderTypes } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getGeoLocationFromStorage } from '../../common/PSXHeader/SearchBar/utils';
import { PortalContext } from '../../context/PortalContext';
import { isMissingParameters } from '../../errors/helpers';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import { ProviderType } from '../../models/ProviderDetails';
import {
  getRecommendationPayload,
  getSortByValue,
} from '../../utils/providerSearch.utils';
import {
  isPlanVariationCodeRequired,
  padPolicyId,
  useIsEligibleForStandaloneDental,
} from '../../utils/user.utils';
import { useFeatureFlag } from '../useFeatureFlag';
import { useLagoon } from '../useLagoon';
import { useProviderDetailsSearch } from '../useProviderDetailsSearch';
import { useSmartChoice } from '../useSmartChoice';
import { StoreKeys } from '../useStore/state';
import { useStore } from '../useStore/useStore';

export const useProviderDetailsWidget = (variables) => {
  const locale = getLanguage()?.code || 'en';
  const {
    claimSystemTypeCode,
    coverageType,
    eligibilitySystemTypeCode,
    latitude,
    lob,
    population,
    locationId,
    longitude,
    reciprocityId,
    planVariationCode,
    planYear,
    policyId,
    providerId,
    providerType,
    shouldGetHGData,
    membershipCategory,
    virtualOnlyLocationInd,
    isWidgetSmartChoiceEnabled,
    billingCodes,
  } = variables;
  const allConfigs = useLagoon('config')();
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const { stateCode } = getGeoLocationFromStorage();
  const [enablePvcCheck] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PVC_CHECK_DETAILS_WIDGET,
  ]);
  const defaultSearchRadius = allConfigs?.find(
    (row) => row.key === 'DEFAULT_SEARCH_RADIUS'
  )?.value;
  const [enableRRVirtualCareCEXWidget, isNewPositionsForSmartChoiceEnabled] =
    useFeatureFlag([
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_RR_VIRTUAL_CARE_CEX_WIDGET,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_POSITIONS_FOR_SMART_CHOICE,
    ]);

  const { portalData } = useContext(PortalContext);

  const [providerDetails, getProviderDetails] = useProviderDetailsSearch(
    {},
    providerType
  );
  const isGuestUser = portalData?.portalConfig?.isGuestUser;
  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();
  const planVariationCodeRequired = isPlanVariationCodeRequired(
    lob,
    population,
    portalData?.portalName,
    false,
    isGuestUser,
    isEligibleForStandaloneDental
  );
  const neccessaryParams = {
    policyId,
    providerType,
    reciprocityId,
    latitude,
    longitude,
    coverageType,
    ...(planVariationCodeRequired && enablePvcCheck
      ? { planVariationCode }
      : {}),
  };

  const isSmartChoice = useSmartChoice();
  const isPractitioner =
    providerType?.toLowerCase() == ProviderTypes.PRACTITIONER;
  const isSmartChoiceEnabled =
    isPractitioner && (isWidget ? isWidgetSmartChoiceEnabled : isSmartChoice);
  const sortByValue = getSortByValue(isSmartChoiceEnabled);

  const recommendationPreferences = {
    ...getRecommendationPayload(),
    billingCode: billingCodes || '',
    llmCodes: billingCodes || '',
    recommendationRadius: portalData?.recommendationRadius
      ? Number(portalData.recommendationRadius)
      : +defaultSearchRadius,
    ...(portalData?.tieredPlan ? { tieredPlan: portalData?.tieredPlan } : {}),
    ...(portalData?.suppressPremium
      ? { suppressPremium: portalData?.suppressPremium }
      : {}),
  };
  useEffect(() => {
    if (isMissingParameters('ProviderDetails', neccessaryParams)) {
      return;
    }

    // Ensure policyId is 7 digit and Format policyId only if enableRRVirtualCareCEXWidget is true
    const formattedPolicyId = enableRRVirtualCareCEXWidget
      ? padPolicyId(policyId)
      : policyId;

    getProviderDetails({
      variables: {
        latitude,
        longitude,
        stateCode,
        locale,
        lob,
        population,
        coverages: [coverageType],
        planYear,
        planVariationCode,
        claimSystemTypeCode,
        eligibilitySystemTypeCode,
        policyId: formattedPolicyId,
        policyID: formattedPolicyId,
        providerId,
        providerType: providerType ?? ProviderType.PRACTITIONER,
        reciprocityId,
        coverageType,
        locationId,
        shouldGetHGData,
        shouldGetLFData: providerType === ProviderType.ORGANIZATION,
        membershipCategory,
        sortBy: sortByValue,
        ...(isSmartChoiceEnabled && {
          recommendationPreferences,
          isProviderRecommendationsEnabled: true,
        }),
        virtualOnlyLocationInd,
        isGuestUser,
        isNewPositionsForSmartChoiceEnabled,
        clientName: portalData?.clientName || ClientNames.PSX,
      },
    });
  }, [
    latitude,
    longitude,
    stateCode,
    providerId,
    coverageType,
    JSON.stringify(recommendationPreferences),
  ]);
  return providerDetails;
};
